import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { useBackgroundDispatch } from 'src/hooks'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import {
  POST_USER_RESEND_VERIFICATION_FAILURE,
  POST_USER_RESEND_VERIFICATION_INIT,
  POST_USER_RESEND_VERIFICATION_SUCCESS,
  POST_USER_VERIFICATION_FAILURE,
  POST_USER_VERIFICATION_INIT,
  POST_USER_VERIFICATION_SUCCESS,
  PostUserVerificationFailureAction,
  PostUserVerificationSuccessAction,
} from 'src/redux/actions/auth.actions'
import {
  BackendErrorPayload,
  getBackendErrorPayload,
} from 'src/utils/error.utils'

type BackendErrorResponse = { response?: { data: BackendErrorPayload<string> } }

type UseUserVerificationVariables = {
  code: string
  email: string
}

/** Submit an email verification code and resolve with the legacy action. */
export const useUserVerification = () => {
  const dispatch = useBackgroundDispatch()

  return useMutation({
    mutationFn: ({
      code,
      email,
    }: UseUserVerificationVariables): Promise<
      PostUserVerificationSuccessAction | PostUserVerificationFailureAction
    > => {
      dispatch({ type: POST_USER_VERIFICATION_INIT })
      return axios
        .post(
          '/users/verify',
          { email, code },
          getRequestConfig(RequestConfig.Unauthed),
        )
        .then(
          (response): PostUserVerificationSuccessAction => {
            const action: PostUserVerificationSuccessAction = {
              type: POST_USER_VERIFICATION_SUCCESS,
              payload: response.data,
            }
            dispatch(action)
            return action
          },
          (err: BackendErrorResponse): PostUserVerificationFailureAction => {
            const action: PostUserVerificationFailureAction = {
              type: POST_USER_VERIFICATION_FAILURE,
              payload: getBackendErrorPayload(err),
            }
            dispatch(action)
            return action
          },
        )
    },
  })
}

/** Resend the email verification code using the legacy reducer contract. */
export const useUserResendVerification = () => {
  const dispatch = useBackgroundDispatch()

  return useMutation({
    mutationFn: (email: string): Promise<void> => {
      dispatch({ type: POST_USER_RESEND_VERIFICATION_INIT })
      return axios
        .post(
          '/users/resendverification',
          { email },
          getRequestConfig(RequestConfig.Unauthed),
        )
        .then(
          (response) => {
            dispatch({
              type: POST_USER_RESEND_VERIFICATION_SUCCESS,
              payload: response.data,
            })
          },
          (err: BackendErrorResponse) => {
            dispatch({
              type: POST_USER_RESEND_VERIFICATION_FAILURE,
              payload: getBackendErrorPayload(err),
            })
          },
        )
    },
  })
}
